import { useEffect, useState } from "react";

import { useDeleteImportBatch } from "../../api/hooks/useDeleteImportBatch";
import { useDeletionImpact } from "../../api/hooks/useDeletionImpact";
import { formatDate } from "../../lib/date";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export type DeleteImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string | null;
  filename: string;
  /** Called after a successful delete so the page can show the Undo toast. */
  onDeleted: (jobId: string) => void;
};

function ImpactRow({
  label,
  value,
  valueTestId,
}: {
  label: string;
  value: string | number;
  valueTestId?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-on-surface-variant">{label}</span>
      <span className="font-medium text-on-surface" data-testid={valueTestId}>
        {value}
      </span>
    </div>
  );
}

export function DeleteImportDialog({
  open,
  onOpenChange,
  jobId,
  filename,
  onDeleted,
}: DeleteImportDialogProps) {
  const [includeModified, setIncludeModified] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const impactQuery = useDeletionImpact(open ? jobId : null);
  const deleteMutation = useDeleteImportBatch();

  useEffect(() => {
    if (open) {
      setIncludeModified(false);
      setSubmitError(null);
    }
  }, [open]);

  const impact = impactQuery.data;
  const netRemoved =
    impact === undefined
      ? null
      : includeModified
        ? impact.removed_if_include_modified
        : impact.removed_if_preserve_modified;

  async function onConfirm() {
    if (jobId === null) {
      return;
    }
    setSubmitError(null);
    try {
      await deleteMutation.mutateAsync({ jobId, includeModified });
      onOpenChange(false);
      onDeleted(jobId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to delete import.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this import?</DialogTitle>
          <DialogDescription>
            The trades this import created will be moved to the trash. You can restore them
            until the undo window closes.
          </DialogDescription>
        </DialogHeader>

        {impactQuery.isLoading && (
          <p className="text-sm text-on-surface-variant">Calculating impact…</p>
        )}
        {impactQuery.isError && (
          <p role="alert" className="text-sm text-error">
            Could not load the import details.
          </p>
        )}
        {impact !== undefined && (
          <div className="space-y-2 rounded-control border border-outline-variant p-3 text-sm">
            <ImpactRow label="File" value={impact.filename || filename} />
            <ImpactRow label="Broker" value={impact.broker} />
            <ImpactRow label="Imported" value={formatDate(impact.imported_at)} />
            {impact.total_rows !== null && (
              <ImpactRow label="Rows" value={impact.total_rows} />
            )}
            <ImpactRow label="Trades still present" value={impact.trades_present} />
            <ImpactRow
              label="Edited or annotated (kept by default)"
              value={impact.trades_modified}
            />
            <div className="border-t border-outline-variant pt-2">
              <ImpactRow
                label="Trades that will be removed"
                value={netRemoved ?? impact.trades_present}
                valueTestId="net-removed"
              />
            </div>
          </div>
        )}

        <label className="flex items-start gap-2 text-sm text-on-surface">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeModified}
            onChange={(e) => setIncludeModified(e.target.checked)}
            disabled={impact !== undefined && impact.trades_modified === 0}
          />
          <span>
            Also delete trades I&rsquo;ve edited or annotated
            {impact !== undefined && impact.trades_modified > 0 && (
              <span className="text-on-surface-variant"> ({impact.trades_modified})</span>
            )}
          </span>
        </label>

        {submitError !== null && (
          <p role="alert" className="text-sm text-error">
            {submitError}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onConfirm()}
            disabled={deleteMutation.isPending || jobId === null}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
